import {
  INVOICE_ACTION,
  INVOICE_ACTION_LABEL,
  INVOICE_EMAIL_LABEL,
  INVOICE_STATUS,
  INVOICE_TYPE,
  InvoiceAction,
  InvoiceActionOption,
  InvoiceRow,
  PR_INVOICE_LOCKED_HOVER_MSG,
} from "./billing-constant";
import { RenderingProviderInfo } from "./claims/updateClaims/PatientDetailsSideSectionForClaim";

/* -------------------------------------------------------------------------- */
/*                            Invoice row actions                             */
/* -------------------------------------------------------------------------- */

/** An invoice still owes money, so payment actions stay meaningful. */
const hasBalance = (row: InvoiceRow) => Number(row?.patientBalance ?? 0) > 0;

/**
 * A Patient Responsibility invoice is generated from the claim data posted by
 * the ERA/EOB, so it may only ever be viewed - editing it would let the invoice
 * drift away from the posted claim it settles.
 */
const isPatientResponsibilityRow = (row: InvoiceRow) =>
  row?.invoiceType === INVOICE_TYPE.PR;

const emailLabel = (row: InvoiceRow, suffix: string) =>
  `${row?.invoiceSentToPatient ? INVOICE_EMAIL_LABEL.RESEND : INVOICE_EMAIL_LABEL.SEND} ${suffix}`;

// Labels are per-action rather than per-status so a status never restates one.
const INVOICE_ACTION_LABEL_BUILDER: Record<
  InvoiceAction,
  (row: InvoiceRow) => string
> = {
  [INVOICE_ACTION.PRINT]: () => INVOICE_ACTION_LABEL.PRINT,
  [INVOICE_ACTION.VIEW]: () => INVOICE_ACTION_LABEL.VIEW,
  [INVOICE_ACTION.EDIT]: () => INVOICE_ACTION_LABEL.EDIT,
  [INVOICE_ACTION.CANCEL]: () => INVOICE_ACTION_LABEL.CANCEL,
  [INVOICE_ACTION.PRINT_CMS]: () => INVOICE_ACTION_LABEL.PRINT_CMS,
  [INVOICE_ACTION.COLLECT_PAYMENT]: () => INVOICE_ACTION_LABEL.COLLECT_PAYMENT,
  [INVOICE_ACTION.SEND_WITH_LINK]: (row) =>
    emailLabel(row, INVOICE_EMAIL_LABEL.WITH_LINK_SUFFIX),
  [INVOICE_ACTION.SEND_WITHOUT_LINK]: (row) =>
    emailLabel(row, INVOICE_EMAIL_LABEL.WITHOUT_LINK_SUFFIX),
  // Create Claim is currently hidden from the invoice menu; claims are raised
  // from the Claims tab instead. Kept so the map stays exhaustive.
  [INVOICE_ACTION.CREATE_CLAIM]: () => "Create Claim",
  [INVOICE_ACTION.VOID_PAYMENT]: () => "Void Payment",
  [INVOICE_ACTION.ARCHIVE]: () => "Archive",
};

/** An action entry, optionally gated on the row. */
type InvoiceActionRule = {
  action: InvoiceAction;
  when?: (row: InvoiceRow) => boolean;
};

const onlyWithBalance = (action: InvoiceAction): InvoiceActionRule => ({
  action,
  when: hasBalance,
});

// Menu contents per invoice status, in display order. This table is the single
// source of truth for both the Billing → Invoices list and the Patient Chart →
// Financial → Invoices mirror, so the two can never drift apart again.
//
// Deliberately excluded everywhere:
// - Create Claim — raised from the Claims tab.
// - Print As CMS 1500 Form — the invoice-side form renders fewer fields than
//   the claim-side one, so it is printed from the Claim tab instead.
const INVOICE_ACTIONS_BY_STATUS: Record<string, InvoiceActionRule[]> = {
  // A cancelled invoice is view-only.
  [INVOICE_STATUS.CANCELLED]: [{ action: INVOICE_ACTION.VIEW }],
  // A paid invoice can no longer be edited, cancelled or collected against.
  [INVOICE_STATUS.PAID]: [
    { action: INVOICE_ACTION.PRINT },
    { action: INVOICE_ACTION.VIEW },
    { action: INVOICE_ACTION.SEND_WITHOUT_LINK },
  ],
  // Partially paid keeps payment actions only while a balance remains.
  [INVOICE_STATUS.PARTIALLY_PAID]: [
    { action: INVOICE_ACTION.PRINT },
    { action: INVOICE_ACTION.VIEW },
    { action: INVOICE_ACTION.SEND_WITHOUT_LINK },
    onlyWithBalance(INVOICE_ACTION.SEND_WITH_LINK),
    onlyWithBalance(INVOICE_ACTION.COLLECT_PAYMENT),
  ],
  // Pending is the only status that may still be edited.
  [INVOICE_STATUS.PENDING]: [
    { action: INVOICE_ACTION.PRINT },
    { action: INVOICE_ACTION.VIEW },
    { action: INVOICE_ACTION.EDIT },
    onlyWithBalance(INVOICE_ACTION.SEND_WITH_LINK),
    { action: INVOICE_ACTION.SEND_WITHOUT_LINK },
    { action: INVOICE_ACTION.CANCEL },
    onlyWithBalance(INVOICE_ACTION.COLLECT_PAYMENT),
  ],
  [INVOICE_STATUS.UNPAID]: [
    { action: INVOICE_ACTION.PRINT },
    { action: INVOICE_ACTION.VIEW },
    onlyWithBalance(INVOICE_ACTION.SEND_WITH_LINK),
    { action: INVOICE_ACTION.SEND_WITHOUT_LINK },
    { action: INVOICE_ACTION.CANCEL },
    onlyWithBalance(INVOICE_ACTION.COLLECT_PAYMENT),
  ],
};

// OVERDUE / DRAFT / a missing status: everything the row supports, minus the
// edit and cancel actions that are reserved for Pending / Unpaid.
const DEFAULT_INVOICE_ACTIONS: InvoiceActionRule[] = [
  { action: INVOICE_ACTION.PRINT },
  { action: INVOICE_ACTION.VIEW },
  onlyWithBalance(INVOICE_ACTION.SEND_WITH_LINK),
  { action: INVOICE_ACTION.SEND_WITHOUT_LINK },
  onlyWithBalance(INVOICE_ACTION.COLLECT_PAYMENT),
];

/**
 * Build the invoice Action column menu for a row.
 *
 * Shared by the Billing module and the Patient Chart → Financial mirror so both
 * offer the same options for the same status. Pair with `useInvoiceRowActions`,
 * which handles the clicks and owns the matching modals.
 */
export const getInvoiceActionOptions = (
  row: InvoiceRow
): InvoiceActionOption[] => {
  const rules =
    INVOICE_ACTIONS_BY_STATUS[row?.superBillStatus ?? ""] ??
    DEFAULT_INVOICE_ACTIONS;

  const editLocked = isPatientResponsibilityRow(row);

  return rules
    .filter(({ when }) => !when || when(row))
    .map(({ action }) => ({
      value: action,
      label: INVOICE_ACTION_LABEL_BUILDER[action](row),
      // Edit stays listed but unusable on a PR invoice, so the reason is
      // visible on hover instead of the option silently disappearing.
      ...(editLocked && action === INVOICE_ACTION.EDIT
        ? { isDisabled: true, hoverMsg: PR_INVOICE_LOCKED_HOVER_MSG }
        : {}),
    }));
};

/**
 * Derive the Rendering Provider Details (Name, Provider ID, NPI, License) from
 * a source record that exposes `renderingProvider` (a
 * `{ [providerId]: providerName }` map), `renderingProviderNpi` and
 * `renderingProviderLicenseNumber`.
 *
 * Used across the Create / Update / View invoice screens so they stay in sync
 * with the Invoice Report. The source `data` differs per screen (encounter
 * codes vs. saved superBill), but the field shape is the same.
 *
 * @param data source record (encounter codes or saved superBill); may be undefined
 * @returns the rendering provider info, or undefined when there is no data
 */
export const getRenderingProviderInfo = (
  data: any
): RenderingProviderInfo | undefined => {
  if (!data) return undefined;
  const [providerId, providerName] = data.renderingProvider
    ? (Object.entries(data.renderingProvider as Record<string, string>)[0] ?? [
        undefined,
        undefined,
      ])
    : [undefined, undefined];
  return {
    name: providerName,
    providerId,
    npi: data.renderingProviderNpi ?? undefined,
    licenseNumber: data.renderingProviderLicenseNumber ?? undefined,
  };
};
